import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export type TenantLocationSummary = {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  city: string | null;
  province: string | null;
  timezone: string | null;
  active: boolean;
  unitCount: number;
  onlineUnitCount: number;
  sportCodes: string[];
};

export type RecentBooking = {
  id: string;
  locationName: string | null;
  sportName: string | null;
  unitName: string | null;
  customerEmail: string | null;
  customerName: string | null;
  status: string;
  totalPrice: number | null;
  platformFee: number | null;
  startTime: string;
  currency: string | null;
};

export type PlatformTenantDetail = {
  id: string;
  name: string;
  slug: string;
  status: 'ACTIVE' | 'PENDING' | 'SUSPENDED';
  planTier: string | null;
  createdAt: string;
  lastActivityAt: string | null;

  contactEmail: string | null;
  contactName: string | null;

  platformFeePercent: number;
  platformFeeOverride: number | null;
  defaultPlatformFee: number;
  publicListing: boolean;
  defaultCurrency: string | null;

  stripeConnectStatus: 'CONNECTED' | 'PENDING' | 'NOT_STARTED';
  stripeAccountId: string | null;
  stripeChargesEnabled: boolean;
  stripePayoutsEnabled: boolean;
  stripeDetailsSubmitted: boolean;

  locations: TenantLocationSummary[];

  totalBookings: number;
  bookingsThisMonth: number;
  totalRevenue: number;
  revenueThisMonth: number;
  totalPlatformFeeCollected: number;
  platformFeeThisMonth: number;

  ttlockCredentialsSet: boolean;
  shellyCredentialsSet: boolean;

  recentBookings: RecentBooking[];
};

export function usePlatformTenantDetail(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['platform-tenant-detail', tenantId],
    queryFn: () => apiClient<PlatformTenantDetail>(`/platform/tenants/${tenantId}`),
    enabled: !!tenantId,
  });
}

export type FeeOverrideRequest =
  | { resetToDefault: true; feePercent?: null }
  | { resetToDefault: false; feePercent: number };

export function useUpdateFeeOverride(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FeeOverrideRequest) =>
      apiClient<{ message: string; platformFeePercent: number; platformFeeOverride: number | null }>(
        `/platform/tenants/${tenantId}/fee-override`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-detail', tenantId] });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
    },
  });
}

export type TtlockCredentialsRequest = {
  clientId: string;
  clientSecret: string;
  username: string;
  passwordMd5: string;
  apiBaseUrl?: string;
};

export type ShellyCredentialsRequest = {
  authKey: string;
  serverUrl?: string;
};

export type CredentialsUpdateResponse = {
  credentialsSet: boolean;
  message: string;
};

export function useUpdateTtlockCredentials(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TtlockCredentialsRequest) =>
      apiClient<CredentialsUpdateResponse>(
        `/platform/tenants/${tenantId}/ttlock-credentials`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-detail', tenantId] });
    },
  });
}

export function useUpdateShellyCredentials(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShellyCredentialsRequest) =>
      apiClient<CredentialsUpdateResponse>(
        `/platform/tenants/${tenantId}/shelly-credentials`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-detail', tenantId] });
    },
  });
}
